import type { IMeta } from '@audacy-clients/core/atoms/wrappers/modules';
import { needsHTMLRendering } from '@audacy-clients/core/utils/strings';
import { Helmet } from 'react-helmet-async';

import favicon from '~/assets/images/favicon.png';
import type { IBranchMetadata } from '~/hooks/use-branch-journey';
import { Colors } from '~/styles';

export interface ISEOProps {
  description?: string;
  title?: string;
  branchMetadata?: IBranchMetadata;
  seoMetadata?: IMeta | null;
}

const defaultMetadata = {
  canonical: '',
  description: '',
  title: '',
  social: { description: '', locale: '', title: '' },
} as IMeta;

const SEO = ({
  description,
  title,
  branchMetadata,
  seoMetadata = defaultMetadata,
}: ISEOProps): JSX.Element => {
  // Meta can come as null.
  const { social, ...standardMeta } = seoMetadata ?? defaultMetadata;

  return (
    <Helmet>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content={Colors.audacyDarkNavy} />
      {title && <title>{title}</title>}
      {title && <meta name="title" content={title} />}
      {description && <meta name="description" content={description} />}
      <link rel="icon" href={favicon} />

      {branchMetadata &&
        (Object.keys(branchMetadata) as Array<keyof IBranchMetadata>).map(
          (key: keyof IBranchMetadata) => {
            const content = branchMetadata[key];
            return content && <meta key={key} name={`branch:deeplink:${key}`} content={content} />;
          },
        )}

      {standardMeta.robots && <meta name="robots" content={standardMeta.robots} />}
      {standardMeta.title && <title>{standardMeta.title}</title>}
      {standardMeta.title && <meta name="title" content={standardMeta.title} />}
      {standardMeta.description && !needsHTMLRendering(standardMeta.description) && (
        <meta name="description" content={standardMeta.description} />
      )}
      {standardMeta.canonical && <link rel="canonical" href={standardMeta.canonical} />}
      {standardMeta.canonical && <meta property="og:url" content={standardMeta.canonical} />}

      {social.title && <meta property="og:title" content={social.title} />}
      {social.description && !needsHTMLRendering(social.description) && (
        <meta property="og:description" content={social.description} />
      )}
      {social.siteName && <meta property="og:site_name" content={social.siteName} />}
      {social.image && <meta property="og:image" content={social.image} />}
      {social.imageHeight && <meta property="og:image:height" content={social.imageHeight} />}
      {social.imageWidth && <meta property="og:image:width" content={social.imageWidth} />}
      {social.locale && <meta property="og:locale" content={social.locale} />}
      {social.type && <meta property="og:type" content={social.type} />}

      <meta name="twitter:card" content="summary_large_image" />
      {/* TODO: [A2-5937] make the below dynamic once it comes from CMS, see XP ticket [A2-5936] */}
      <meta name="twitter:site" content="@Audacy" />
    </Helmet>
  );
};

export default SEO;
